<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import ConversationMonitoringAPI from 'dashboard/api/conversationMonitoring';
import ConversationAPI from 'dashboard/api/inbox/conversation';
import { useAlert } from 'dashboard/composables';
import { messageStamp } from 'shared/helpers/timeHelper';
import { formatReportDuration } from '../settings/reports/helpers/reportFormatHelper';
import CustomerServiceWorkspaceNav from 'dashboard/components-next/navigation/CustomerServiceWorkspaceNav.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const { t } = useI18n();
const router = useRouter();
const store = useStore();
const overview = ref(null);
const windows = ref([]);
const loading = ref(true);
const loadError = ref(false);
const jobHealth = ref([]);
const auditEvents = ref([]);
const selectedStatus = ref('all');
const agentId = ref(null);
const teamId = ref(null);
const inboxId = ref(null);
const takingOverId = ref(null);
const settingsDialogRef = ref(null);
const settingsSaving = ref(false);
const responseSettings = ref({
  warning_minutes: 5,
  timeout_minutes: 8,
  escalation_minutes: 10,
});
let refreshTimer;

const agents = computed(() => store.getters['agents/getAgents'] || []);
const teams = computed(() => store.getters['teams/getTeams'] || []);
const inboxes = computed(() => store.getters['inboxes/getInboxes'] || []);
const isAdmin = computed(
  () => store.getters.getCurrentRole === 'administrator'
);
const currentUserId = computed(() => store.getters.getCurrentUserID);
const requestParams = computed(() => ({
  ...(agentId.value && { agent_id: agentId.value }),
  ...(teamId.value && { team_id: teamId.value }),
  ...(inboxId.value && { inbox_id: inboxId.value }),
}));
const statusOptions = computed(() => [
  ['all', t('MONITORING.STATUS.ALL')],
  ['backlog', t('MONITORING.STATUS.BACKLOG')],
  ['waiting', t('MONITORING.STATUS.WAITING')],
  ['warning', t('MONITORING.STATUS.WARNING')],
  ['timeout', t('MONITORING.STATUS.TIMEOUT')],
  ['escalated', t('MONITORING.STATUS.ESCALATED')],
]);
const metricItems = computed(() => [
  ['online_agents', t('MONITORING.METRICS.ONLINE')],
  ['backlog_count', t('MONITORING.METRICS.BACKLOG')],
  ['waiting_count', t('MONITORING.METRICS.WAITING')],
  [
    'warning_count',
    t('MONITORING.METRICS.WARNING', {
      minutes: responseSettings.value.warning_minutes,
    }),
  ],
  [
    'timeout_count',
    t('MONITORING.METRICS.TIMEOUT', {
      minutes: responseSettings.value.timeout_minutes,
    }),
  ],
  [
    'escalation_count',
    t('MONITORING.METRICS.ESCALATED', {
      minutes: responseSettings.value.escalation_minutes,
    }),
  ],
]);
const settingsDescription = computed(() =>
  t('MONITORING.SETTINGS_DESCRIPTION', {
    warning: responseSettings.value.warning_minutes,
    timeout: responseSettings.value.timeout_minutes,
    escalation: responseSettings.value.escalation_minutes,
  })
);

const metricValue = key => {
  if (key === 'online_agents')
    return overview.value?.online_agents?.length || 0;
  return overview.value?.[key] || 0;
};
const duration = seconds => formatReportDuration(seconds, t);
const statusLabel = item => {
  if (item.escalated) return t('MONITORING.STATUS.ESCALATED');
  if (item.status === 'timeout') return t('MONITORING.STATUS.TIMEOUT');
  if (item.status === 'warning') return t('MONITORING.STATUS.WARNING');
  if (item.status === 'waiting') return t('MONITORING.STATUS.WAITING');
  return t('MONITORING.STATUS.BACKLOG');
};
const conversationLabel = item => `${item.contact_name} #${item.display_id}`;
const ownerLabel = item =>
  [item.assignee_name || t('MONITORING.PUBLIC_QUEUE'), item.team_name]
    .filter(Boolean)
    .join(' · ');
const statusClass = item => {
  if (item.escalated) return 'border-n-ruby-7 bg-n-ruby-2 text-n-ruby-11';
  if (item.status === 'timeout')
    return 'border-n-ruby-6 bg-n-ruby-2 text-n-ruby-11';
  if (item.status === 'warning')
    return 'border-n-amber-6 bg-n-amber-2 text-n-amber-11';
  return 'border-n-weak bg-n-surface-2 text-n-slate-11';
};

const loadSettings = async () => {
  const response = await ConversationMonitoringAPI.settings();
  responseSettings.value = response.data;
};

const openSettings = async () => {
  settingsDialogRef.value?.open();
  await loadSettings();
};

const load = async () => {
  loading.value = true;
  loadError.value = false;
  try {
    const [overviewResponse, windowsResponse] = await Promise.all([
      ConversationMonitoringAPI.overview(requestParams.value),
      ConversationMonitoringAPI.responseWindows({
        ...requestParams.value,
        ...(selectedStatus.value !== 'all' && {
          status: selectedStatus.value,
        }),
      }),
    ]);
    overview.value = overviewResponse.data;
    windows.value = windowsResponse.data || [];
  } catch {
    overview.value = null;
    windows.value = [];
    loadError.value = true;
  } finally {
    loading.value = false;
  }
};

const loadOperations = async () => {
  try {
    const [healthResponse, auditResponse] = await Promise.all([
      ConversationMonitoringAPI.jobHealth(),
      ConversationMonitoringAPI.auditEvents(),
    ]);
    jobHealth.value = healthResponse.data.jobs || [];
    auditEvents.value = auditResponse.data || [];
  } catch {
    jobHealth.value = [];
    auditEvents.value = [];
  }
};

const healthClass = status => {
  if (status === 'healthy') return 'text-n-teal-11';
  if (status === 'running') return 'text-n-brand';
  return 'text-n-ruby-11';
};

const jobLastRun = job =>
  `${t('OPERATIONS.LAST_RUN')}: ${job.last_started_at || t('OPERATIONS.NEVER')}`;
const jobSummary = job =>
  `${t('OPERATIONS.DELAY')}: ${job.delay_seconds ?? '--'} ${t('OPERATIONS.SECONDS')}; ${t('OPERATIONS.FAILURES')}: ${job.failure_count}`;
const auditLabel = event => `${event.operator_name} · ${event.event_label}`;

const saveSettings = async () => {
  settingsSaving.value = true;
  try {
    const response = await ConversationMonitoringAPI.updateSettings({
      warning_minutes: Number(responseSettings.value.warning_minutes),
      timeout_minutes: Number(responseSettings.value.timeout_minutes),
      escalation_minutes: Number(responseSettings.value.escalation_minutes),
    });
    responseSettings.value = response.data;
    settingsDialogRef.value?.close();
    useAlert(t('MONITORING.SAVE_SUCCESS'));
    await load();
  } catch (error) {
    useAlert(error.response?.data?.message || t('MONITORING.SAVE_ERROR'));
  } finally {
    settingsSaving.value = false;
  }
};

const openConversation = item => {
  router.push({
    name: 'inbox_conversation',
    params: {
      accountId: router.currentRoute.value.params.accountId,
      conversation_id: item.display_id,
    },
  });
};

const takeOver = async item => {
  takingOverId.value = item.display_id;
  try {
    await ConversationAPI.assignAgent({
      conversationId: item.display_id,
      agentId: currentUserId.value,
      assigneeType: 'User',
    });
    await load();
  } finally {
    takingOverId.value = null;
  }
};

const changeStatus = status => {
  selectedStatus.value = status;
  load();
};

onMounted(async () => {
  await Promise.allSettled([
    store.dispatch('agents/get'),
    store.dispatch('teams/get'),
    store.dispatch('inboxes/get'),
    loadSettings(),
  ]);
  await load();
  await loadOperations();
  refreshTimer = window.setInterval(load, 30_000);
});

onBeforeUnmount(() => window.clearInterval(refreshTimer));
</script>

<template>
  <div class="h-full w-full overflow-auto bg-n-surface-1 px-6 py-5">
    <div class="mx-auto max-w-[1500px] space-y-5 pb-12">
      <header class="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 class="text-xl font-semibold text-n-slate-12">
            {{ $t('MONITORING.TITLE') }}
          </h1>
          <p class="mt-1 max-w-3xl text-sm text-n-slate-10">
            {{ $t('MONITORING.SUBTITLE') }}
          </p>
        </div>
        <button
          v-if="isAdmin"
          type="button"
          class="button button--secondary"
          @click="openSettings"
        >
          {{ $t('MONITORING.SETTINGS_BUTTON') }}
        </button>
      </header>

      <CustomerServiceWorkspaceNav
        active-page="monitoring"
        show-management-pages
      />

      <section class="rounded-lg border border-n-weak bg-n-surface-2 p-4">
        <h2 class="font-medium text-n-slate-12">
          {{ $t('OPERATIONS.HEALTH_TITLE') }}
        </h2>
        <p class="mt-1 text-sm text-n-slate-10">
          {{ $t('OPERATIONS.HEALTH_DESCRIPTION') }}
        </p>
        <div class="mt-4 grid gap-3 md:grid-cols-2">
          <div
            v-for="job in jobHealth"
            :key="job.job_name"
            class="rounded-lg border border-n-weak p-3"
          >
            <div class="flex items-center justify-between gap-3">
              <strong class="text-sm text-n-slate-12">
                {{ job.display_name }}
              </strong>
              <span
                class="text-sm font-medium"
                :class="healthClass(job.status)"
              >
                {{ job.status_label }}
              </span>
            </div>
            <p class="mt-2 text-xs text-n-slate-10">{{ jobLastRun(job) }}</p>
            <p class="mt-1 text-xs text-n-slate-10">{{ jobSummary(job) }}</p>
            <p v-if="job.error_message" class="mt-1 text-xs text-n-ruby-11">
              {{ job.error_message }}
            </p>
          </div>
        </div>
      </section>

      <section class="rounded-lg border border-n-weak bg-n-surface-2 p-4">
        <h2 class="font-medium text-n-slate-12">
          {{ $t('OPERATIONS.AUDIT_TITLE') }}
        </h2>
        <p class="mt-1 text-sm text-n-slate-10">
          {{ $t('OPERATIONS.AUDIT_DESCRIPTION') }}
        </p>
        <div class="mt-3 divide-y divide-n-weak">
          <div
            v-for="event in auditEvents"
            :key="event.id"
            class="flex flex-wrap justify-between gap-2 py-2 text-sm"
          >
            <span class="text-n-slate-12">{{ auditLabel(event) }}</span>
            <span class="text-n-slate-10">{{ event.occurred_at }}</span>
          </div>
          <p v-if="!auditEvents.length" class="py-4 text-sm text-n-slate-10">
            {{ $t('OPERATIONS.AUDIT_EMPTY') }}
          </p>
        </div>
      </section>

      <section class="rounded-lg border border-n-weak bg-n-surface-2 px-4 py-3">
        <p class="text-sm font-medium text-n-slate-12">
          {{ $t('MONITORING.RESPONSIBILITY_TITLE') }}
        </p>
        <p class="mt-1 text-sm leading-6 text-n-slate-10">
          {{ $t('MONITORING.RESPONSIBILITY_DESCRIPTION') }}
        </p>
      </section>

      <Dialog
        v-if="isAdmin"
        ref="settingsDialogRef"
        type="edit"
        width="2xl"
        :title="$t('MONITORING.SETTINGS_TITLE')"
        :description="settingsDescription"
        :confirm-button-label="$t('MONITORING.SAVE')"
        :is-loading="settingsSaving"
        @confirm="saveSettings"
      >
        <div class="mt-4 grid gap-3 md:grid-cols-3">
          <label class="text-sm text-n-slate-11">
            {{ $t('MONITORING.WARNING_MINUTES') }}
            <input
              v-model.number="responseSettings.warning_minutes"
              class="mt-1 w-full"
              type="number"
              min="1"
            />
          </label>
          <label class="text-sm text-n-slate-11">
            {{ $t('MONITORING.TIMEOUT_MINUTES') }}
            <input
              v-model.number="responseSettings.timeout_minutes"
              class="mt-1 w-full"
              type="number"
              min="2"
            />
          </label>
          <label class="text-sm text-n-slate-11">
            {{ $t('MONITORING.ESCALATION_MINUTES') }}
            <input
              v-model.number="responseSettings.escalation_minutes"
              class="mt-1 w-full"
              type="number"
              min="3"
            />
          </label>
        </div>
      </Dialog>

      <div class="flex flex-wrap gap-2">
        <select v-model="agentId" class="min-w-40" @change="load">
          <option :value="null">
            {{ $t('MONITORING.FILTERS.ALL_AGENTS') }}
          </option>
          <option v-for="agent in agents" :key="agent.id" :value="agent.id">
            {{ agent.name }}
          </option>
        </select>
        <select v-model="teamId" class="min-w-40" @change="load">
          <option :value="null">
            {{ $t('MONITORING.FILTERS.ALL_TEAMS') }}
          </option>
          <option v-for="team in teams" :key="team.id" :value="team.id">
            {{ team.name }}
          </option>
        </select>
        <select v-model="inboxId" class="min-w-40" @change="load">
          <option :value="null">
            {{ $t('MONITORING.FILTERS.ALL_INBOXES') }}
          </option>
          <option v-for="inbox in inboxes" :key="inbox.id" :value="inbox.id">
            {{ inbox.name }}
          </option>
        </select>
      </div>

      <div
        v-if="loading && !overview"
        class="py-12 text-center text-n-slate-10"
      >
        {{ $t('MONITORING.LOADING') }}
      </div>
      <section
        v-else-if="loadError"
        role="alert"
        class="rounded-lg border border-n-ruby-6 bg-n-ruby-2 px-5 py-10 text-center"
      >
        <p class="text-sm font-medium text-n-ruby-11">
          {{ $t('MONITORING.LOAD_ERROR') }}
        </p>
        <button
          type="button"
          class="button button--secondary mt-4"
          @click="load"
        >
          {{ $t('MONITORING.RETRY') }}
        </button>
      </section>
      <template v-else>
        <section class="grid grid-cols-2 gap-3 lg:grid-cols-3 xl:grid-cols-6">
          <div
            v-for="item in metricItems"
            :key="item[0]"
            class="rounded-lg border border-n-weak bg-n-surface-2 p-4"
          >
            <p class="text-xs text-n-slate-10">{{ item[1] }}</p>
            <strong class="mt-1 block text-2xl text-n-slate-12">
              {{ metricValue(item[0]) }}
            </strong>
          </div>
        </section>

        <section
          class="overflow-hidden rounded-lg border border-n-weak bg-n-surface-2"
        >
          <div
            class="flex flex-wrap items-center justify-between gap-3 border-b border-n-weak p-4"
          >
            <p class="text-sm text-n-slate-10">
              {{ $t('MONITORING.OPTIONAL_TAKEOVER') }}
            </p>
            <div class="flex flex-wrap gap-1">
              <button
                v-for="option in statusOptions"
                :key="option[0]"
                type="button"
                class="button button--clear button--small"
                :class="{ 'bg-n-brand-2': selectedStatus === option[0] }"
                @click="changeStatus(option[0])"
              >
                {{ option[1] }}
              </button>
            </div>
          </div>
          <div
            v-if="!windows.length"
            class="px-4 py-10 text-center text-sm text-n-slate-10"
          >
            {{ $t('MONITORING.EMPTY') }}
          </div>
          <article
            v-for="item in windows"
            :key="item.id"
            class="grid gap-4 border-b border-n-weak p-4 last:border-0 lg:grid-cols-[minmax(0,1fr)_auto_auto] lg:items-center"
          >
            <div class="min-w-0">
              <div class="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  class="truncate text-left text-sm font-medium text-n-slate-12 hover:text-n-brand"
                  @click="openConversation(item)"
                >
                  {{ conversationLabel(item) }}
                </button>
                <span
                  class="rounded-full border px-2 py-0.5 text-xs"
                  :class="statusClass(item)"
                >
                  {{ statusLabel(item) }}
                </span>
              </div>
              <p class="mt-1 text-xs text-n-slate-10">
                {{ ownerLabel(item) }}
              </p>
              <p class="mt-1 text-xs text-n-slate-9">
                {{
                  $t('MONITORING.CUSTOMER_MESSAGE_AT', {
                    time: messageStamp(
                      item.waiting_started_at,
                      'yyyy年M月d日 HH:mm:ss'
                    ),
                  })
                }}
              </p>
            </div>
            <strong class="text-sm text-n-ruby-11">
              {{
                $t('MONITORING.WAITED', {
                  time: duration(item.effective_wait_seconds),
                })
              }}
            </strong>
            <div class="flex items-center justify-end gap-2">
              <button
                type="button"
                class="button button--secondary button--small"
                @click="openConversation(item)"
              >
                {{ $t('MONITORING.VIEW') }}
              </button>
              <span
                v-if="item.assignee_id === currentUserId"
                class="text-xs text-n-slate-10"
              >
                {{ $t('MONITORING.MY_CONVERSATION') }}
              </span>
              <button
                v-else
                type="button"
                class="button button--primary button--small"
                :disabled="takingOverId === item.display_id"
                @click="takeOver(item)"
              >
                {{ $t('MONITORING.TAKE_OVER') }}
              </button>
            </div>
          </article>
        </section>
      </template>
    </div>
  </div>
</template>
